
/**
 * AssetGroup Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { AssetGroupSchema, CreateAssetGroupSchema, UpdateAssetGroupSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';

export class AssetGroupService {
    constructor(api) {
        this.api = api;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await this.api.getAll(params);
            return {
                success: true,
                data: response.data,
                meta: response.pagination || {}, // Map pagination to meta to match Integration expectation
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'AssetGroup listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset-group list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await this.api.getById(id);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset-group');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const validation = CreateAssetGroupSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.create(validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetGroup oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create asset-group');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const validation = UpdateAssetGroupSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.update(id, validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetGroup güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update asset-group');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetGroup silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete asset-group');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            return await this.batchDelete(ids);
        } catch (error) {
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }
            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Batch delete items
     * @param {Array<string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete');
        }
    }

    /**
     * Custom operation: createBatchDelete (Legacy/Old name)
     */
    async createBatchDelete(data = {}) {
        return this.batchDelete(data.ids || []);
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
